/**
 * Settings tab of the school management screen (ADM-8): school profile, AI
 * policy, and support.
 */
import React from 'react';
import { View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Text } from '@/components/ui/text';
import { showDialog } from '@/utils/dialogs';
import SchoolAIPolicyEditor from '../../../components/school/SchoolAIPolicyEditor';
import { useTranslation } from '../../../hooks/useTranslation';
import { type School } from '../schoolManagementTypes';

export function SettingsTab({ school }: { school: School | null }) {
  const { t } = useTranslation();

  return (
    <View className="mb-6">
      <Text className="text-xl font-bold text-foreground mb-4">
        {t('schoolManagement.settingsSection.title')}
      </Text>

      {/* School Profile Card */}
      <Card className="mb-4">
        <CardHeader>
          <Text className="text-lg font-bold text-foreground">
            {t('schoolManagement.settingsSection.profileTitle')}
          </Text>
        </CardHeader>
        <CardContent className="p-4">
          <View className="gap-4">
            <View>
              <Text className="text-sm font-medium text-foreground mb-2">
                {t('schoolManagement.settingsSection.schoolName')}
              </Text>
              <Text className="text-base text-muted-foreground">{school?.name}</Text>
              <Text className="text-xs text-muted-foreground mt-1">
                {t('schoolManagement.settingsSection.schoolNameSupport')}
              </Text>
            </View>

            <View>
              <Text className="text-sm font-medium text-foreground mb-2">
                {t('schoolManagement.settingsSection.schoolId')}
              </Text>
              <Text className="text-base text-muted-foreground">{school?.id}</Text>
            </View>

            <View>
              <Text className="text-sm font-medium text-foreground mb-2">
                {t('schoolManagement.settingsSection.slug')}
              </Text>
              <Text className="text-base text-muted-foreground">{school?.slug}</Text>
            </View>

            <View>
              <Text className="text-sm font-medium text-foreground mb-2">
                {t('schoolManagement.settingsSection.subscriptionType')}
              </Text>
              <View
                className={`px-3 py-1 rounded-full self-start ${school?.subscription_status === 'active' ? 'bg-green-100' : 'bg-yellow-100'
                  }`}
              >
                <Text
                  className={`text-sm font-semibold ${school?.subscription_status === 'active' ? 'text-green-700' : 'text-yellow-700'
                    }`}
                >
                  {school?.subscription_type || 'N/A'} ({school?.subscription_status || t('schoolManagement.settingsSection.subscriptionStatusFallback')})
                </Text>
              </View>
            </View>

            <View>
              <Text className="text-sm font-medium text-foreground mb-2">
                {t('schoolManagement.settingsSection.studentCapacity')}
              </Text>
              <Text className="text-base text-muted-foreground">
                {t('schoolManagement.settingsSection.studentCapacityValue', {
                  current: school?.current_students || 0,
                  max: school?.max_students || 0,
                })}
              </Text>
            </View>
          </View>
        </CardContent>
      </Card>

      {/* AI Policy Editor — ADUI-03, ADUI-04 */}
      {school?.id && <SchoolAIPolicyEditor schoolId={school.id} />}

      {/* Help Card */}
      <Card>
        <CardHeader>
          <Text className="text-lg font-bold text-foreground">
            {t('schoolManagement.settingsSection.helpTitle')}
          </Text>
        </CardHeader>
        <CardContent className="p-4">
          <Text className="text-sm text-muted-foreground mb-4">
            {t('schoolManagement.settingsSection.helpDescription')}
          </Text>
          <Button
            variant="default"
            size="sm"
            onPress={() =>
              showDialog(
                t('schoolManagement.settingsSection.supportDialogTitle'),
                t('schoolManagement.settingsSection.supportDialogMessage')
              )
            }
          >
            <MaterialIcons name="support-agent" size={16} color="white" />
            <Text className="text-white ml-2">
              {t('schoolManagement.settingsSection.contactSupport')}
            </Text>
          </Button>
        </CardContent>
      </Card>
    </View>
  );
}
